import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { AuthService } from '../../services/authService';
import { User } from '@supabase/supabase-js';
import TermsAcceptanceModal from '../../components/auth/TermsAcceptanceModal';
import { setAuthInTransition, clearValidationCache } from '../../utils/sessionValidationUtil';

// Function to ensure user profile exists after SSO login
const ensureUserProfile = async (user: User): Promise<boolean> => {
  if (!user?.id) return false;

  try {

    // Check if profile exists
    const { data: existingProfile, error: profileError } = await supabase
      .from('users')
      .select('id, terms_accepted')
      .eq('id', user.id)
      .single();



    // If profile doesn't exist, create it (this is a new user)
    if (!existingProfile) {


      // Extract name from user metadata if available
      const fullName = user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        '';

      // Extract avatar from user metadata if available
      const avatarUrl = user.user_metadata?.avatar_url || null;

      // Generate username from email or name
      const username = user.user_metadata?.preferred_username ||
        (user.email ? user.email.split('@')[0] : `user_${user.id.substring(0, 8)}`);

      // Create profile with upsert to avoid conflicts (terms_accepted defaults to false)
      await supabase.from('users').upsert([{
        id: user.id,
        email: user.email,
        username: username,
        display_name: fullName,
        avatar_url: avatarUrl,
        role: 'entrepreneur', // Default role
        points: 0,    // Starting points
        created_at: new Date().toISOString(),
        completed_tasks: 0,
        terms_accepted: false // New users haven't accepted terms yet
      }], { onConflict: 'id' });


      return true; // This is a new user
    }

    // Check if existing user has accepted terms
    return !existingProfile.terms_accepted; // Return true if they need to accept terms
  } catch (err) {
    console.error('Error ensuring user profile:', err);
    return false;
  }
};

const AuthCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [newUserId, setNewUserId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Mark auth as in transition to prevent validation failures during callback processing
        setAuthInTransition(true);
        // Clear any stale validation cache from previous session
        clearValidationCache();

        // Get the auth code from the URL
        const hash = window.location.hash;
        const query = window.location.search;

        console.log('AuthCallback: Processing authentication...', { hasHash: !!hash, hasQuery: !!query });

        // First, check if we have hash-based tokens (magic link, password reset, etc.)
        if (hash) {
          console.log('AuthCallback: Hash detected, waiting for Supabase to process tokens...');

          // Wait a bit for Supabase to process the hash automatically
          await new Promise(resolve => setTimeout(resolve, 500));

          // Try multiple times to get the session (Supabase processes hash in background)
          let sessionData = null;
          let attempts = 0;
          const maxAttempts = 5;

          while (attempts < maxAttempts && !sessionData) {
            attempts++;
            console.log(`AuthCallback: Attempt ${attempts}/${maxAttempts} to get session...`);

            const { data, error: sessionError } = await supabase.auth.getSession();

            if (sessionError) {
              console.error('Error getting session:', sessionError);
              if (attempts === maxAttempts) {
                setError('Authentication failed. Please try again or request a new link.');
                return;
              }
            } else if (data?.session) {
              console.log('AuthCallback: ✅ Session established from hash!');
              sessionData = data;
              break;
            }

            // Wait before retry
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }

          if (!sessionData?.session) {
            console.error('AuthCallback: Failed to establish session after all attempts');
            setError('Authentication link may have expired. Please request a new one.');
            return;
          }

          // Session established successfully
          const session = sessionData.session;
          console.log('AuthCallback: Session user:', session.user.email);

          // Create user profile if it doesn't exist and check if this is a new user or needs terms
          const needsTermsAcceptance = await ensureUserProfile(session.user);

          // Get display name for the modal
          const userName = session.user.user_metadata?.full_name ||
            session.user.user_metadata?.name ||
            session.user.email?.split('@')[0] ||
            'User';

          // If this user needs to accept terms, show the modal
          if (needsTermsAcceptance) {
            console.log('New user needs to accept terms');
            setNewUserId(session.user.id);
            setDisplayName(userName);
            setShowTermsModal(true);
            return; // Don't navigate yet, wait for terms acceptance
          }

          // If terms already accepted, proceed normally
          console.log('Existing user with accepted terms, proceeding to app');

          // Store in localStorage to trigger tour after navigation for truly new users
          localStorage.setItem('trigger_onboarding_tour', 'true');
          localStorage.setItem('new_user_id', session.user.id);

          // Clean the URL by removing the hash
          window.history.replaceState(null, '', '/');

          // Mark auth transition as complete before navigating
          // Small delay to ensure session is fully propagated
          setTimeout(() => {
            setAuthInTransition(false);
            clearValidationCache(); // Clear cache to use fresh session
            navigate('/', { replace: true });
          }, 500);
          return;
        }

        // Handle query-based OAuth callbacks (Google, LinkedIn, etc.)
        if (query) {
          console.log('AuthCallback: Query parameters detected, getting session');

          // Exchange the code for a session
          const { data, error: sessionError } = await supabase.auth.getSession();

          if (sessionError) {
            console.error('Error getting session:', sessionError);
            setError('Authentication failed. Please try again.');
            return;
          }

          if (data?.session) {
            console.log('AuthCallback: Session established from OAuth');

            // Create user profile if it doesn't exist and check if this is a new user or needs terms
            const needsTermsAcceptance = await ensureUserProfile(data.session.user);

            // Get display name for the modal
            const userName = data.session.user.user_metadata?.full_name ||
              data.session.user.user_metadata?.name ||
              data.session.user.email?.split('@')[0] ||
              'User';

            // If this user needs to accept terms, show the modal
            if (needsTermsAcceptance) {
              console.log('New SSO user needs to accept terms');
              setNewUserId(data.session.user.id);
              setDisplayName(userName);
              setShowTermsModal(true);
              return; // Don't navigate yet, wait for terms acceptance
            }

            // If terms already accepted, proceed normally
            console.log('Existing user with accepted terms, proceeding to app');

            // Store in localStorage to trigger tour after navigation for truly new users
            localStorage.setItem('trigger_onboarding_tour', 'true');
            localStorage.setItem('new_user_id', data.session.user.id);

            // Mark auth transition as complete before navigating
            setTimeout(() => {
              setAuthInTransition(false);
              clearValidationCache(); // Clear cache to use fresh session
              navigate('/', { replace: true });
            }, 500);
            return;
          }
        }

        // If no hash or query, or session not established
        if (!hash && !query) {
          setAuthInTransition(false);
          setError('No authentication data found in URL');
          return;
        }

        // If we got here, authentication failed
        console.warn('Session not established in callback');
        setAuthInTransition(false);
        setError('Authentication incomplete. Please try again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } catch (err) {
        console.error('Exception in auth callback:', err);
        setAuthInTransition(false);
        setError('An unexpected error occurred. Please try again.');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const handleTermsAccept = async () => {
    if (!newUserId) {
      setError('User ID not found. Please try logging in again.');
      return;
    }

    try {
      // Update user's terms acceptance
      const { success, error: termsError } = await AuthService.updateTermsAcceptance(newUserId);

      if (!success || termsError) {
        console.error('Failed to update terms acceptance:', termsError);
        setError('Failed to save terms acceptance. Please try again.');
        return;
      }

      console.log('Terms accepted successfully for SSO user');

      // Close modal and proceed to app
      setShowTermsModal(false);

      // Trigger onboarding tour for new users
      localStorage.setItem('trigger_onboarding_tour', 'true');
      localStorage.setItem('new_user_id', newUserId);

      // Mark auth transition as complete before navigating
      setTimeout(() => {
        setAuthInTransition(false);
        clearValidationCache(); // Clear cache to use fresh session
        navigate('/', { replace: true });
      }, 500);
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError('An error occurred while saving your preferences.');
    }
  };

  const handleTermsDecline = () => {
    // If user declines terms, sign them out and redirect to login
    setAuthInTransition(false);
    clearValidationCache();
    supabase.auth.signOut();
    navigate('/login');
  };

  // Show terms modal for new SSO users
  if (showTermsModal) {
    return (
      <TermsAcceptanceModal
        isOpen={true}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
        allowClose={false}
        userName={displayName}
      />
    );
  }

  // Show loading or error state
  return (
    <div className="auth-callback-container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="text-center">
        {error ? (
          <div className="alert alert-danger" role="alert">
            {error}
            <div className="mt-3">
              <button
                className="btn btn-primary"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Completing authentication, please wait...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
